import { CaretDownFilled, CreditCardOutlined, FileDoneOutlined, MailOutlined, StrikethroughOutlined } from '@ant-design/icons';
import { Button, Dropdown, message } from 'antd';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import FileSaver from 'file-saver';
import { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import receivablePaymentLetterApi from '../../apis/ReceivablePaymentLetterApi';
import CustomContext from '../../contexts/CustomContext';
import { Receivable, ReceivablePaymentLetter } from '../../models/Entities';
import alertService from '../../services/AlertService';
import receivableService from '../../services/ReceivableService';
import rolesService from '../../services/RolesService';
import EmbargoBankAccountInfoRequestModal from './EmbargoBankAccountInfoRequestModal/EmbargoBankAccountInfoRequestModal';
import ReceivableNotificationSentsModal from './ReceivableNotificationSentsModal/ReceivableNotificationSentsModal';
import ReceivableNotificationSignsModal from './ReceivableNotificationSignsModal/ReceivableNotificationSignsModal';
import ReceivablePublicAnnouncementsPublishedModal from './ReceivablePublicAnnouncementsPublishedModal/ReceivablePublicAnnouncementsPublishedModal';

const ReceivableActionsMenu = (props: Props) => {
    const { receivables, onAction } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const { auth } = useContext(CustomContext);
    const [notificationSignsModalVisible, setNotificationSignsModalVisible] = useState<boolean>(false);
    const [notificationSentsModalVisible, setNotificationSentsModalVisible] = useState<boolean>(false);
    const [publicAnnouncementsPublishedModalVisible, setPublicAnnouncementsPublishedModalVisible] = useState<boolean>(false);
    const [embargoBankAccountInfoRequestModalVisible, setEmbargoBankAccountInfoRequestModalVisible] = useState<boolean>(false);

    /*** METHODS ***/

    const save = async () => {
        setNotificationSignsModalVisible(false);
        setNotificationSentsModalVisible(false);
        setPublicAnnouncementsPublishedModalVisible(false);
        setEmbargoBankAccountInfoRequestModalVisible(false);
        onAction();
    };

    const createPaymentLetter = async () => {
        try {
            const receivablePaymentLetter: ReceivablePaymentLetter = { receivableIds: receivables.map((r) => r.id!) };
            const document = await receivablePaymentLetterApi.create(receivablePaymentLetter);
            FileSaver.saveAs(document, intl.formatMessage({ id: 'receivable.paymentLetter.file.name' }));
            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error: any) {
            alertService.displayError(error, intl);
        }
    };

    /*** VISUAL ***/

    const isAdmin = rolesService.hasAnyRole(auth, ['ROLE_ADMIN']);
    const isCouncilUser = !isAdmin && rolesService.hasAnyRole(auth, ['ROLE_COUNCIL_USER']);
    const items: CustomItemType[] = [
        {
            key: 'payment-letter',
            icon: <CreditCardOutlined />,
            onClick: createPaymentLetter,
            label: <FormattedMessage id="receivables.actions.paymentLetter" />,
            disabled: !receivableService.isCreatePaymentLetterEnabled(auth, receivables),
            visible: isAdmin
        },
        {
            key: 'notification-sign',
            icon: <StrikethroughOutlined />,
            onClick: () => setNotificationSignsModalVisible(true),
            label: <FormattedMessage id="receivables.actions.notificationSign" />,
            disabled: !receivableService.isCreateNotificationSignEnabled(auth, receivables),
            visible: isCouncilUser
        },
        {
            key: 'notification-sent',
            icon: <MailOutlined />,
            onClick: () => setNotificationSentsModalVisible(true),
            label: <FormattedMessage id="receivables.actions.notificationSent" />,
            disabled: !receivableService.isCreateNotificationSentEnabled(auth, receivables),
            visible: isAdmin
        },
        {
            key: 'publication-published',
            icon: <FileDoneOutlined />,
            onClick: () => setPublicAnnouncementsPublishedModalVisible(true),
            label: <FormattedMessage id="receivables.actions.publicAnnouncementPublished" />,
            disabled: !receivableService.isCreatePublicAnnouncementPublishedEnabled(auth, receivables),
            visible: isAdmin
        },
        {
            key: 'embargo-bank-account-info-request',
            icon: <FileDoneOutlined />,
            onClick: () => setEmbargoBankAccountInfoRequestModalVisible(true),
            label: <FormattedMessage id="receivables.actions.embargoBankAccountInfoRequest" />,
            disabled: !receivableService.isCreateEmbargoBankAccountInfoRequestApiEnabled(auth, receivables),
            visible: isAdmin
        }
    ].filter((item) => item.visible);

    return (
        <>
            <Dropdown trigger={['click']} placement="bottomRight" menu={{ items }}>
                <Button type="primary" size="large">
                    <FormattedMessage id="button.actions" /> <CaretDownFilled />
                </Button>
            </Dropdown>
            {notificationSignsModalVisible && (
                <ReceivableNotificationSignsModal
                    receivableIds={receivables.map((r) => r.id!)}
                    onCancel={() => setNotificationSignsModalVisible(false)}
                    onSave={save}
                />
            )}
            {notificationSentsModalVisible && (
                <ReceivableNotificationSentsModal
                    receivableIds={receivables.map((r) => r.id!)}
                    onCancel={() => setNotificationSentsModalVisible(false)}
                    onSave={save}
                />
            )}
            {publicAnnouncementsPublishedModalVisible && (
                <ReceivablePublicAnnouncementsPublishedModal
                    receivableIds={receivables.map((r) => r.id!)}
                    onCancel={() => setPublicAnnouncementsPublishedModalVisible(false)}
                    onSave={save}
                />
            )}
            {embargoBankAccountInfoRequestModalVisible && (
                <EmbargoBankAccountInfoRequestModal
                    councilId={receivables[0].councilId!}
                    receivableIds={receivables.map((r) => r.id!)}
                    onCancel={() => setEmbargoBankAccountInfoRequestModalVisible(false)}
                    onSave={save}
                />
            )}
        </>
    );
};

export default ReceivableActionsMenu;

interface Props {
    receivables: Receivable[];
    onAction: () => Promise<void>;
}

type CustomItemType = ItemType & { visible: boolean };
